import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { createPageUrl } from "../utils";
import { ArrowLeft } from "lucide-react";
import Button from "../components/ui/Button";
import Footer from "../components/home/Footer";

import cast from "../img/products/pipes/cast.webp";
import di from "../img/products/pipes/di.webp";
import ductileflanged from "../img/products/pipes/ductile-flanged.avif";
import ductileiron from "../img/products/pipes/ductile-iron-fitting.jpg";
import ductile from "../img/products/pipes/ductile.png";
import fittings from "../img/products/pipes/fittings.webp";
import ironflanged from "../img/products/pipes/iron-flanges.jpg";
import mechanical from "../img/products/pipes/mechanical-joint-inamge_1.jpeg";
import rubber from "../img/products/pipes/rubber-flange-gasket-373428676-doul8.avif";
import tyton from "../img/products/pipes/tyton.jpg";

/* -----------------------------------
   PRODUCT DATA
----------------------------------- */

const products = [
  {
    name: "Ductile Iron Fittings",
    category: "Fittings",
    image: ductileiron,
    description:
      "Complete range of ductile iron fittings for pipe connections.",
    sizeRange: "80mm to 1200mm",
    joints: "Push-on / Tyton, Flange, Mechanical, Plain End",
    fittings:
      "Bends, Elbows, Tees, Reducers, Collars, Couplings, Adaptors, Flanges, End Caps, Plugs",
  },
  {
    name: "Ductile Iron Flanged Pipe",
    category: "Flanged Pipes",
    image: ductileflanged,
    description:
      "Flanged ductile iron pipes for high-pressure and critical applications.",
    sizeRange: "80mm to 1200mm",
    types: "Screwed, Welded & Casted",
    flangedTypes:
      "Flange Socket Pipe with Puddle, Double Flange Pipe, Double Flanged Pipe with Puddle Flange",
  },
  {
    name: "D.I. Mechanical Fittings",
    category: "Accessories",
    image: di,
    description: "Mechanical fittings and repair accessories.",
    sizeRange: "80mm to 1200mm",
    fittings: "MJ Collar, Dismantling Joint, Flange Adaptor, Leak Repair Clamp",
  },
  {
    name: "Cast Iron Fittings",
    category: "Fittings",
    image: fittings,
    description:
      "Versatile cast iron fittings for drainage and pipeline systems.",
    sizeRange: "80mm to 1500mm",
    fittings: "T-Bend, Reducer, Collar",
  },
  {
    name: "Ductile Iron Spun Pipes",
    category: "Pipes",
    image: ductile,
    description:
      "High-quality ductile iron spun pipes for water supply and sewerage applications.",
    sizeRange: "80mm to 1200mm",
    joints: "Tyton Joint",
  },
  {
    name: "Cast Iron Spun Pipes",
    category: "Pipes",
    image: cast,
    description:
      "Reliable cast iron spun pipes for building drainage and soil & waste systems.",
    sizeRange: "80mm to 1000mm",
    joints: "Tyton Joint / Lead Joint",
  },

  {
    name: "Cast Iron Flanged Pipe",
    category: "Flanged Pipes",
    image: ironflanged,
    description:
      "Cast iron single and double flanged pipes for industrial use.",
    sizeRange: "80mm to 1500mm",
    flangedTypes:
      "Cast Iron Double Flanged Pipe, Cast Iron Single Flange Pipe, Double Flange Pipe with Puddle Flange",
  },

  {
    name: "Tyton Joint Gasket",
    category: "Gaskets",
    image: tyton,
    description: "Rubber gaskets for Tyton joint pipe connections.",
    sizeRange: "80mm to 1200mm",
    material: "EPDM, SBR, Nitrile, TPU, Neoprene",
  },
  {
    name: "Mechanical Joint Gasket",
    category: "Gaskets",
    image: mechanical,
    description: "High performance mechanical joint gaskets.",
    sizeRange: "80mm to 1200mm",
    material: "EPDM, Nitrile, Neoprene, SBR",
  },
  {
    name: "Flange Rubber Gaskets",
    category: "Gaskets",
    image: rubber,
    description: "Premium rubber gaskets for flange joints.",
    sizeRange: "80mm to 1200mm",
    material: "EPDM, Nitrile, Neoprene, SBR",
  },
];

/* -----------------------------------
   COMPONENT
----------------------------------- */

const PipesFittingsProducts = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER */}
      <div className="bg-gradient-to-br from-[#0d47a1] via-[#1976d2] to-[#0d47a1] text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Link to={createPageUrl("Home")}>
            <Button
              variant="ghost"
              className="text-white hover:text-[#f9d71c] mb-8 -ml-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="h-1 w-16 bg-[#f9d71c]" />
              <span className="text-[#f9d71c] font-semibold tracking-wider uppercase text-sm">
                Products
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl font-light mb-4">
              Ductile Iron{" "}
              <span className="font-semibold">Pipes & Fittings</span>
            </h1>

            <p className="text-white/90 text-lg max-w-3xl leading-relaxed">
              Premium quality spun pipes, precision-engineered fittings and
              accessories for water and sewerage infrastructure.
            </p>
          </motion.div>
        </div>
      </div>

      {/* PRODUCTS GRID */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <motion.div
              key={product.name}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
            >
              {/* IMAGE */}
              <div className="h-64 bg-white flex items-center justify-center overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-contain p-4
                             transition-transform duration-300
                             group-hover:scale-105"
                />
              </div>

              {/* CONTENT */}
              <div className="p-6">
                <span className="text-[#1976d2] text-xs font-medium uppercase tracking-wider">
                  {product.category}
                </span>

                <h3 className="text-xl font-semibold text-[#1a237e] mt-2 mb-2">
                  {product.name}
                </h3>

                <p className="text-gray-600 text-sm mb-4">
                  {product.description}
                </p>

                <div className="space-y-2 text-sm text-gray-600">
                  {product.sizeRange && (
                    <p>
                      <strong>Size Range:</strong> {product.sizeRange}
                    </p>
                  )}

                  {product.joints && (
                    <p>
                      <strong>Types of Joints:</strong> {product.joints}
                    </p>
                  )}

                  {product.types && (
                    <p>
                      <strong>Type:</strong> {product.types}
                    </p>
                  )}

                  {product.fittings && (
                    <p>
                      <strong>Types of Fittings:</strong> {product.fittings}
                    </p>
                  )}

                  {product.flangedTypes && (
                    <p>
                      <strong>Flanged Types:</strong> {product.flangedTypes}
                    </p>
                  )}

                  {product.material && (
                    <p>
                      <strong>Material:</strong> {product.material}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-16 bg-[#1a237e] rounded-3xl p-8 md:p-12 text-center"
        >
          <h3 className="text-2xl md:text-3xl font-light text-white mb-4">
            Need Custom{" "}
            <span className="font-medium text-[#f9d71c]">Specifications?</span>
          </h3>

          <p className="text-white/70 max-w-2xl mx-auto mb-6">
            All products can be customized in size and material as per your
            requirements.
          </p>

          <Link to="/#contact">
            <Button className="bg-[#f9d71c] text-[#1a237e] hover:bg-[#f9d71c]/90 px-8 py-6 text-lg font-medium rounded-full">
              Request Quote
            </Button>
          </Link>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default PipesFittingsProducts;
